import { useEffect, useState } from 'react';
import Image from 'components/shared/image';
import Icon from 'components/shared/icon';
import BackChevron from 'components/shared/back-chevron';
import { formatPriceByStoreLocale } from 'lib/shopify';
import { useGlobalModules } from 'contexts';
import { findGiftLineForGroup } from 'lib/cart/incentives/reconcileGiftLines';
import SlidePanel from '../../slidePanel/slidePanel';
import styles from './giftChoicePanel.module.scss';

const GiftChoicePanel = ({
  isOpen,
  onClose,
  onCloseDrawer,
  locale,
  cart,
  choiceGroups,
  selectGift,
}) => {
  const globalModules = useGlobalModules();
  const { translations, colors } = globalModules || {};
  const [contentfulData, setContentfulData] = useState([]);
  const [pending, setPending] = useState(null);

  const pickGift = async (group, product) => {
    setPending({ discountId: group.discountId, variantId: product.variantId });
    try {
      await selectGift(group, product);
    } finally {
      setPending(null);
    }
  };

  // price/size data for the gift products — same endpoint as freeItemPopup.jsx:42-72
  useEffect(() => {
    if (!isOpen || !choiceGroups?.length) return;
    const IDs = choiceGroups.map(g => g.freeProducts.map(({ id }) => id)).flat();
    if (!IDs.length) return;
    const fetchProducts = async () => {
      const res = await fetch('/api/contentful/get-products-by-ids', {
        method: 'POST',
        headers: { 'Content-type': 'application/json' },
        body: JSON.stringify({ locale, productIds: IDs }),
      });
      if (res.ok) setContentfulData(await res.json());
    };
    fetchProducts();
  }, [isOpen, choiceGroups?.length]);

  return (
    <SlidePanel
      isOpen={isOpen}
      nested
      onClose={onClose}
      ariaLabel={translations?.freeItem?.buttonText}
    >
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <button type="button" className={styles.backBtn} onClick={onClose} aria-label="Back">
            <BackChevron width={16} height={16} />
            <span className={styles.title}>{translations?.freeItem?.buttonText}</span>
          </button>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onCloseDrawer}
            aria-label={translations?.accessibility?.closeCart || 'Close'}
          >
            <Icon type="close" />
          </button>
        </div>
        {translations?.freeItem?.discountTitle ? (
          <p className={styles.description}>
            {translations.freeItem.discountTitle.replace('{count}', '')}
          </p>
        ) : null}
      </div>
      <div className={styles.options}>
        {choiceGroups.map(group => {
          const selected = findGiftLineForGroup(cart?.lines?.edges, group);
          return (
            <div key={group.discountId} className={styles.group}>
              {group.discountTitle ? (
                <p className={styles.groupTitle}>{group.discountTitle}</p>
              ) : null}
              {group.freeProducts.map(product => {
                const cData = contentfulData.find(({ slug }) => product.handle === slug);
                const price = cData?.shopifyData?.variants?.[0]?.price;
                const isSelected =
                  pending?.discountId === group.discountId
                    ? pending.variantId === product.variantId
                    : selected?.merchandise?.id === product.variantId;
                return (
                  <div
                    key={product.id}
                    className={styles.option}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    onClick={() => pickGift(group, product)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') pickGift(group, product);
                    }}
                  >
                    <div className={styles.optionImage}>
                      <Image src={product.featuredImage} layout="fill" />
                    </div>
                    <div className={styles.optionInfo}>
                      <div className={styles.optionTitle}>{product.title}</div>
                      <div className={styles.optionPrice}>
                        {price ? (
                          <span className={styles.strikePrice}>
                            {formatPriceByStoreLocale({
                              locale,
                              price: Number(parseFloat(price)).toFixed(2),
                            })}
                          </span>
                        ) : null}
                        <span
                          className={styles.freeText}
                          style={{ color: colors?.freeItem?.priceFreeTextColor || '' }}
                        >
                          {translations?.freeItem?.priceFreeText || ''}
                        </span>
                      </div>
                    </div>
                    <input
                      className={styles.radio}
                      type="radio"
                      tabIndex={-1}
                      checked={isSelected}
                      onChange={() => {}}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className={styles.footer}>
        <button type="button" className={styles.closeAction} onClick={onClose}>
          {translations?.freeItem?.closePanelText ||
            translations?.accessibility?.closeCart ||
            'Close'}
        </button>
      </div>
    </SlidePanel>
  );
};

export default GiftChoicePanel;
